/* eslint-disable max-lines-per-function */
import { Badge, Grid, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon, Timeline } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconClock, IconDatabase, IconExternalLink, IconNetwork, IconStatusChange } from '@tabler/icons-react';

import { formatDate, getStatusColor } from '../request-logger.utils';

import type { RequestDetailsResponse } from '@admin/hooks';
interface OverviewTabProps {
  data: RequestDetailsResponse;
}

export const OverviewTab = ({ data }: OverviewTabProps) => {
  const { request, externalCalls, dbChanges, timeline } = data;

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'request':
        return <IconArrowRight size={16} />;
      case 'external-call':
        return <IconExternalLink size={16} />;
      case 'db-change':
        return <IconDatabase size={16} />;
      case 'response':
        return <IconArrowLeft size={16} />;
      default:
        return <IconClock size={16} />;
    }
  };

  return (
    <Stack gap="md">
      <SimpleGrid cols={3}>
        <Paper withBorder p="md">
          <Group gap="xs" mb="xs">
            <ThemeIcon variant="light" size="lg">
              <IconNetwork size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">
                Type
              </Text>
              <Text size="sm" fw={600}>
                {request.requestType}
              </Text>
            </div>
          </Group>
        </Paper>

        <Paper withBorder p="md">
          <Group gap="xs" mb="xs">
            <ThemeIcon variant="light" size="lg" color={getStatusColor(request.response?.status)}>
              <IconStatusChange size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">
                Status
              </Text>
              <Badge color={getStatusColor(request.response?.status)}>{request.response?.status || 'N/A'}</Badge>
            </div>
          </Group>
        </Paper>

        <Paper withBorder p="md">
          <Group gap="xs" mb="xs">
            <ThemeIcon variant="light" size="lg">
              <IconClock size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">
                Timestamp
              </Text>
              <Text size="xs" fw={600} style={{ fontFamily: 'monospace' }}>
                {formatDate(request.createdAt)}
              </Text>
            </div>
          </Group>
        </Paper>
      </SimpleGrid>

      <Paper withBorder p="md">
        <Text size="sm" fw={600} mb="md">
          Timeline
        </Text>
        <Timeline active={timeline.length} bulletSize={24} lineWidth={2}>
          {timeline.map((event, index) => (
            <Timeline.Item key={index} bullet={getTimelineIcon(event.type)} title={event.title}>
              <Text c="dimmed" size="xs">
                {formatDate(event.timestamp)}
              </Text>
            </Timeline.Item>
          ))}
        </Timeline>
      </Paper>

      <Grid>
        <Grid.Col span={6}>
          <Paper withBorder p="md">
            <Text size="sm" fw={600} mb="xs">
              Request Info
            </Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  Method
                </Text>
                <Badge size="sm">{request.request?.method}</Badge>
              </Group>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  URL
                </Text>
                <Text size="xs" style={{ fontFamily: 'monospace', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {request.request?.url}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  IP Address
                </Text>
                <Text size="xs" style={{ fontFamily: 'monospace' }}>
                  {request.request?.ip}
                </Text>
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={6}>
          <Paper withBorder p="md">
            <Text size="sm" fw={600} mb="xs">
              Summary
            </Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  External Calls
                </Text>
                <Badge size="sm" variant="light">
                  {externalCalls.length}
                </Badge>
              </Group>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  DB Changes
                </Text>
                <Badge size="sm" variant="light">
                  {dbChanges.length}
                </Badge>
              </Group>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  Request ID
                </Text>
                <Text size="xs" style={{ fontFamily: 'monospace' }}>
                  {request.requestId?.substring(0, 8)}...
                </Text>
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
